import { cacheSingle } from '@rivethealth/core/cache';
import { mapObject } from '@rivethealth/core/object';
import { Comparable, sort } from '@rivethealth/core/sort';
import { Direction, Order, Query } from '@rivethealth/data/super-table';
import Fuse from 'fuse.js';
import { identity, of } from 'rxjs';
import { DataSource } from './data-source';

/**
 * DataSource operating on in-memory data.
 */
export class LocalDataSource<T, Filter> implements DataSource<T, Filter> {
  private getFilteredData = cacheSingle((filters: Filter) => {
    let filteredData = this.data;
    for (const filter in filters) {
      if (filter in this.params.filters) {
        filteredData = this.params.filters[filter](
          filteredData,
          filters[filter],
        );
      }
    }
    return filteredData;
  });

  private getSearchedData = cacheSingle((filteredData: T[], search: string) =>
    this.params.search(filteredData, search),
  );

  private getSortedData = cacheSingle((searchedData: T[], sort: Order) =>
    this.params.sorters[sort.name](searchedData, sort.direction),
  );

  constructor(
    private readonly data: T[],
    private readonly params: LocalDataSource.Params<T, Filter>,
  ) {}

  query(query: Query<Filter>) {
    const filteredData = this.getFilteredData(query.filters);

    let searchedData = this.getSearchedData(filteredData, query.search);
    searchedData = this.params.search(filteredData, query.search);
    if (
      query.sort &&
      query.sort.name in this.params.sorters &&
      !(query.sort.name == 'default' && query.search) // Search results are already sorted by best match, which should take priority over the default sort.
    ) {
      searchedData = this.getSortedData(searchedData, query.sort);
    }

    let rangedData: T[];
    if (query.range) {
      const start =
        query.range.start < this.data.length ? query.range.start : 0;
      rangedData = searchedData.slice(start, start + query.range.count);
    } else {
      rangedData = searchedData;
    }

    return of({
      data: rangedData,
      noRange: {
        count: searchedData.length,
      },
      noFilterSearch: {
        empty: !this.data.length,
      },
      noSearch: {
        empty: !filteredData.length,
      },
    });
  }
}

export namespace LocalDataSource {
  export interface Params<T, Filter> {
    filters: { [K in keyof Filter]: (data: T[], filter: Filter[K]) => T[] };
    sorters: { [key: string]: (data: T[], direction: Direction) => T[] };
    search: (data: T[], search: string) => T[];
  }

  export interface StandardParams<T, Filter = {}> {
    filters?: { [K in keyof Filter]: (data: T, filter: Filter[K]) => boolean };
    sorters: {
      [key: string]: (datum: T) => Comparable;
    };
    search?: {
      keys: string[] | { name: string; weight: number }[];
      exact: boolean;
    };
  }

  export function standard<T, Filter>(
    data: T[],
    params: StandardParams<T, Filter>,
  ) {
    return new LocalDataSource(data, standardParams(params));
  }

  function standardParams<T, Filter>(
    params: StandardParams<T, Filter>,
  ): LocalDataSource.Params<T, Filter> {
    const filters = params.filters
      ? mapObject(
          params.filters,
          filter => (data: T[], filterData: Filter[keyof Filter]) =>
            data.filter(datum => filter(datum, filterData)),
        )
      : <any>{};

    let searchCache: { fuse: Fuse<T>; data: T[] } | undefined;
    const search = params.search
      ? (data: T[], search: string) => {
          if (!search) {
            return data;
          }
          if (!searchCache || searchCache.data !== data) {
            searchCache = {
              data,
              fuse: new Fuse(data, {
                keys: params.search!.keys,
                shouldSort: true,
                ignoreLocation: true,
                threshold: params.search!.exact ? 0 : 0.2,
              }),
            };
          }
          return searchCache.fuse.search(search).map(result => result.item);
        }
      : identity;

    const sorters = mapObject(
      params.sorters,
      sorter => (data: T[], direction: Direction) =>
        sort(data, combineSorters([sorter, params.sorters.default]), direction),
    );

    return {
      filters,
      search,
      sorters,
    };
  }
}

function combineSorters<T>(
  sorters: ((datum: T) => Comparable)[],
): (datum: T) => Comparable {
  return datum => {
    const result: Comparable[] = [];
    for (const sorter of sorters) {
      if (!sorter) continue;
      const key = sorter(datum);
      if (key instanceof Array) {
        result.push(...key);
      } else {
        result.push(key);
      }
    }
    return result;
  };
}
